import { useQuery } from '@tanstack/react-query';
import { atom, ExtractAtomValue, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import {
  fetchLegalStructureGraph,
  ILegalStructureEdge,
  ILegalStructureEntityRow,
  ILegalStructureGraphResponse,
  LegalStructureEntityKind,
  LegalStructureNodeType,
} from '../../../../services/queries/MaggieLegalStructureQueries';
import { fundsAtom } from '../../../../services/state/AppConfigStateJ';
import { useLegalStructureRegister } from '../../hooks/useLegalStructureRegister';
import { dateToPlainDateString } from '../../../../util/date-utilts';
import { portfolioCompaniesLoadableAtom } from '../../../../services/state/PortCosStateJ';
import { FMT } from '../../../../util/formatter-service';
import { useEntityManagementAsOfDate } from '../../state/EntityManagementState';
import { withDerivedLanes } from './deriveLanes';
import { deriveFamilyBands, familyByMember } from './deriveFamilyBands';
import { mergeCoHolders } from './coHolders';
import { useCoHolders } from './useCoHolders';
import { KNOWN_RELATIONSHIP_TYPES, ROLE_BY_ENTITY_ROLE, toRole } from './nodeMapping';
import { toLegalName } from './nodeTitle';
import { IStructureEdge, IStructureGraph, IStructureLane, IStructureNode, RelationshipType } from './types';
import { ChartRoot } from './useStructureChartUrlState';

/**
 * The structure chart's graph (FD-4465).
 *
 * Reads `GET /legalStructure/graph` (api#4605, merged 2026-08-14 as 303a6790b).
 *
 * **One fund at a time.** BE confirmed (2026-08-17) there is no whole-firm view in
 * v1 — "we always load a single fund at max at a time" — so the chart selects a fund
 * rather than merging every fund's graph. The caller supplies the fund id.
 *
 * The read endpoints carry only AuthGuard — the LaunchDarkly gate covers the mutation
 * controllers, not these — so an empty result means the tenant has no legal-structure
 * data, not that it is switched off. In that case the chart shows the portfolio
 * companies the app already has
 * — real names, real locations, and **no edges**, because ownership between entities is
 * exactly what that endpoint knows and this one does not. A chart of invented ownership
 * is worse than a chart with none.
 */

const EMPTY_GRAPH: IStructureGraph = { nodes: [], edges: [], lanes: [] };

// The join key lives in its own module so the URL layer can share it without a cycle.
import { nodeKey } from './nodeKey';

export { nodeKey };

/**
 * Joins the graph's topology with the register's legal record.
 *
 * The graph knows the shape; the register knows what each entity *is* — the
 * edge-derived role that separates a feeder from a main fund, and the legal form and
 * jurisdiction the card chips show. Neither read carries both.
 */
export function toStructureGraph(
  response: ILegalStructureGraphResponse,
  register: ILegalStructureEntityRow[] = [],
  companies: Pick<ICompanyDataModel, 'id' | 'legalName'>[] = []
): IStructureGraph {
  const registerByRef = new Map(register.map((row) => [nodeKey(row.ref.nodeType, row.ref.nodeId), row]));
  const companyById = new Map(companies.map((company) => [company.id, company]));

  const nodes: IStructureNode[] = response.nodes.map((node) => {
    const id = nodeKey(node.ref.nodeType, node.ref.nodeId);
    const row = registerByRef.get(id);
    // The register's role is taken for *funds*, where it is edge-derived and separates
    // a feeder from a main fund.
    //
    // Company nodes keep the graph's kind. `listEntities` queries
    // `where: { companyType: 'legal_entity' }`, so the register holds vehicles only and
    // a portfolio company is simply absent from it — its `?? 'HOLDING_CO'` fallback is
    // correct for the rows it does return. The graph includes operating companies and
    // resolves them via `companyType`, so it is the right source for that role. This
    // stays explicit rather than relying on the lookup missing, so a later change to
    // the register's row scope cannot silently relabel every portfolio company.
    const registerRole = row && node.ref.nodeType === 'fund' ? ROLE_BY_ENTITY_ROLE[row.role] : null;
    // The register holds no portfolio companies, so theirs comes off the company list. Gated
    // on kind so a vehicle sharing a company id can never take a portfolio company's name.
    const isPortfolioCompany = node.ref.nodeType === 'company' && node.kind === 'PORTFOLIO_COMPANY';
    const legalName = row
      ? row.legalName
      : isPortfolioCompany
        ? companyById.get(node.ref.nodeId)?.legalName
        : null;

    return {
      id,
      name: node.name,
      legalName: toLegalName(legalName),
      kind: 'HOLDING_CO',
      role: registerRole ?? toRole(node),
      legalForm: row?.legalForm ?? undefined,
      taxClassification: row?.taxClassification ?? undefined,
      jurisdiction: row?.jurisdiction ?? undefined,
      // The instrument chips (FD-4482). Both come off the register rather than a per-node
      // read, so chipping every card costs nothing beyond the call the chart already makes.
      debt: row?.hostedDebt ?? undefined,
      hostsPec: row?.hostsPec ?? undefined,
    };
  });

  const edges: IStructureEdge[] = response.edges.flatMap((edge) => {
    const relationshipType = edge.relationshipType as RelationshipType;
    // Drawing an unknown type under a guessed grammar would state something untrue
    // about the relationship.
    if (!KNOWN_RELATIONSHIP_TYPES.has(relationshipType)) {
      return [];
    }

    return [
      {
        id: String(edge.id),
        source: nodeKey(edge.parentType, edge.parentId),
        target: nodeKey(edge.childType, edge.childId),
        relationshipType,
        ownershipPercentage: edge.ownershipPercentage,
        votingPercentage: edge.votingPercentage,
        principalAmount: edge.principalAmount,
        interestRate: edge.interestRate,
        effectiveFrom: edge.effectiveFrom,
        effectiveTo: edge.effectiveTo,
        // The DTO's `source` is provenance; `source` on IStructureEdge is the source
        // node. See the field comment in types.ts.
        provenance: edge.source,
        notes: edge.notes,
        parentRef: { nodeType: edge.parentType, nodeId: edge.parentId },
        childRef: { nodeType: edge.childType, nodeId: edge.childId },
      },
    ];
  });

  // Lanes come from the company grouping in the payload, which this rooted read does
  // not carry — see the lane-membership open question in the spec.
  const lanes: IStructureLane[] = [];

  /*
   * Families are read from the *unfiltered* payload edges, not from `edges` above.
   *
   * MEMBERSHIP is dropped from the drawn set a few lines up, because the chart never joins
   * a family to its funds with a connector. The grouping still has to come from somewhere,
   * so it is derived here from what the API actually sent.
   */
  const families = deriveFamilyBands(response.nodes, response.edges);
  const familyOf = familyByMember(families);
  nodes.forEach((node) => {
    const familyId = familyOf.get(node.id);
    if (familyId) {
      node.familyId = familyId;
    }
  });

  return { nodes, edges, lanes, families };
}

/**
 * Portfolio companies as bare nodes: real names and locations, no ownership.
 *
 * Used only when the legal-structure endpoint is absent.
 */
export function toCompanyOnlyGraph(companies: ICompanyDataModel[]): IStructureGraph {
  return {
    nodes: companies.map((company) => ({
      id: `company-${company.id}`,
      name: company.name,
      legalName: toLegalName(company.legalName),
      kind: 'PORTFOLIO_COMPANY',
      role: 'PORTFOLIO_COMPANY',
      operatingLocation: FMT.format('locationShort', company) || undefined,
    })),
    edges: [],
    lanes: [],
  };
}

/**
 * Funds the chart can be rooted at.
 *
 * `/configuration` returns every row in the fund table — `FundService.findAll()` is
 * unfiltered — and that includes the intermediary funds the legal-structure module
 * creates to pair with a holding vehicle (`fundKind: 'intermediary'`, set by
 * `LegalEntityService`). Those are holding entities carrying a fund row, not funds
 * anyone would choose to root a chart at; the metrics pipeline already drops them via
 * `intermediary-exclusion.helper`.
 *
 * The app's own fund model has no `fundKind`, so the register is the only place the
 * distinction is visible to the client. With no register the list is unfiltered —
 * offering every fund beats offering none.
 */
/** Roots list A to Z: the app config hands funds over in creation order, which reads as none. */
const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

export function selectRootFunds<T extends { id: number; name: string; fundKind?: string }>(
  funds: T[],
  register: ILegalStructureEntityRow[] | null | undefined
): T[] {
  const sorted = [...funds].filter((fund) => fund.fundKind !== 'family').sort(byName);
  if (!register?.length) {
    return sorted;
  }

  const intermediaryFundIds = new Set(
    register
      .filter((row) => row.ref.nodeType === 'fund' && row.fundKind === 'intermediary')
      .map((row) => row.ref.nodeId)
  );

  return intermediaryFundIds.size === 0 ? sorted : sorted.filter((fund) => !intermediaryFundIds.has(fund.id));
}

/** Register families as pickable chart roots, A to Z. */
export function selectRootFamilies(
  register: ILegalStructureEntityRow[] | null | undefined
): { id: number; name: string }[] {
  return (register ?? [])
    .filter((row) => row.ref.nodeType === 'family')
    .map((row) => ({ id: row.ref.nodeId, name: row.name }))
    .sort(byName);
}

export function useStructureChartFamilies(enabled = true): { id: number; name: string }[] {
  const { data: register } = useLegalStructureRegister(enabled);

  return useMemo(() => selectRootFamilies(register), [register]);
}

export function useStructureChartRootFunds(enabled = true): { id: number; name: string }[] {
  const funds = useAtomValue(fundsAtom);
  const { data: register } = useLegalStructureRegister(enabled);

  return useMemo(() => selectRootFunds(funds, register), [funds, register]);
}

export interface IStructureChartGraphResult {
  graph: IStructureGraph;
  isLoading: boolean;
  /** True when the legal-structure endpoint is not deployed. */
  isStructureUnavailable: boolean;
  /**
   * The endpoint answered and the fund has no relationships on the as-of date.
   *
   * `buildGraph` walks `entity_relationship` outward from the root, so a fund with no
   * rows comes back as itself and nothing else — a single card with no lines, which
   * reads as a broken chart unless the surface says otherwise.
   *
   * Taken from the response rather than the mapped graph: if the API grows a
   * relationship type the chart has no line for, `toStructureGraph` drops it and the
   * chart is empty, but "no relationships recorded" would be untrue.
   */
  hasNoRelationships: boolean;
  /**
   * At least one co-holder read failed, so the outside holders drawn are some of them
   * rather than all (FD-4933). The chart says so rather than looking complete.
   */
  isCoHoldersIncomplete?: boolean;
}

export interface ISubjectOption {
  id: string;
  name: string;
  kind: LegalStructureEntityKind;
}

// Picker group order: firm level first, then the vehicles, then the companies they hold.
const SUBJECT_KIND_ORDER: LegalStructureEntityKind[] = [
  'FUND',
  'GP_ENTITY',
  'MANAGEMENT_CO',
  'HOLDING_CO',
  'BLOCKER',
  'SPV',
  'PORTFOLIO_COMPANY',
];

/**
 * Every entity the chart can take as a subject: the register (families excluded, since
 * membership is not an equity edge and their stack is empty) unioned with the portfolio
 * companies, which are not register rows. Sorted group-contiguously in
 * `SUBJECT_KIND_ORDER`, names alphabetical within a group, so the picker's `groupBy`
 * renders each heading exactly once.
 */
export function buildSubjectOptions(
  register: ILegalStructureEntityRow[],
  portfolioCompanies: { id: number; name: string }[]
): ISubjectOption[] {
  const byId = new Map<string, ISubjectOption>();
  register
    .filter((row) => row.kind !== 'FAMILY')
    .forEach((row) =>
      byId.set(`${row.ref.nodeType}-${row.ref.nodeId}`, {
        id: `${row.ref.nodeType}-${row.ref.nodeId}`,
        name: row.name,
        kind: row.kind,
      })
    );
  portfolioCompanies.forEach((company) => {
    const id = `company-${company.id}`;
    if (!byId.has(id)) {
      byId.set(id, { id, name: company.name, kind: 'PORTFOLIO_COMPANY' });
    }
  });
  return [...byId.values()].sort(
    (a, b) =>
      SUBJECT_KIND_ORDER.indexOf(a.kind) - SUBJECT_KIND_ORDER.indexOf(b.kind) || a.name.localeCompare(b.name)
  );
}

/** `buildSubjectOptions` over the live reads; undefined until both sources have answered. */
/** Stands in for the companies read when the options are not wanted, so nothing is fetched for them. */
const NO_COMPANIES_LOADABLE = atom<ExtractAtomValue<typeof portfolioCompaniesLoadableAtom>>({
  state: 'hasData',
  data: [],
});

export function useSubjectOptions(enabled = true): ISubjectOption[] | undefined {
  const companies = useAtomValue(enabled ? portfolioCompaniesLoadableAtom : NO_COMPANIES_LOADABLE);
  const { data: register } = useLegalStructureRegister(enabled);

  return useMemo(() => {
    if (register == null || companies.state !== 'hasData') {
      return undefined;
    }
    return buildSubjectOptions(register, companies.data);
  }, [register, companies]);
}

export function useStructureChartGraph(root?: ChartRoot, subjectId?: string): IStructureChartGraphResult {
  const companies = useAtomValue(portfolioCompaniesLoadableAtom);
  const rootFunds = useStructureChartRootFunds();
  const [asOfDate] = useEntityManagementAsOfDate();

  // The subject outranks the root: the two answer different questions, and the subject
  // param is the one a reader chose last (setting either clears the other's dependants
  // in the URL).
  const isSubjectView = subjectId != null;
  // The param's shape is validated at the URL boundary, so the split is trustworthy here.
  const subjectType = isSubjectView ? (subjectId.split('-')[0] as LegalStructureNodeType) : undefined;
  const subjectNodeId = isSubjectView ? Number(subjectId.split('-')[1]) : undefined;

  // Rooted at the selection — a family since FD-4918, a fund before that — defaulting to
  // the first fund so the surface renders something before the user picks.
  const rootType = isSubjectView && subjectType ? subjectType : (root?.nodeType ?? 'fund');
  const rootId = isSubjectView ? subjectNodeId : (root?.nodeId ?? rootFunds[0]?.id);

  // Plain calendar date, from local components. `toISOString()` would convert to UTC
  // and shift the date by a day for anyone west of UTC in the evening — transactions
  // are timezone independent, so 2026-08-14 is that day everywhere.
  const asOf = dateToPlainDateString(asOfDate);

  const { data: register } = useLegalStructureRegister();

  const { data, isLoading: isGraphLoading } = useQuery({
    queryKey: ['legalStructureGraph', isSubjectView ? `subject:${subjectId}` : rootType, rootId, asOf],
    queryFn: () =>
      fetchLegalStructureGraph({
        rootType,
        rootId: rootId as number,
        asOf,
        // Both directions: the subject's holders above and holdings below, one picture.
        ...(isSubjectView ? { direction: 'both' as const } : {}),
      }),
    enabled: rootId != null,
    retry: false,
  });

  /*
   * Outside co-holders (FD-4933).
   *
   * `buildGraph` walks a fund or family root forward only, so a vehicle that holds one of
   * this chart's holdcos without descending from any of its funds — a single-LP co-invest,
   * typically — is never in the payload. This asks for them and merges them in.
   *
   * Not run in the entity view: that read already asks for `direction: 'both'` and has the
   * subject's holders.
   */
  const coHolders = useCoHolders(data, asOf, !isSubjectView);

  return useMemo(
    () =>
      toGraphChartResult(
        data,
        register,
        companies.state === 'hasData' ? companies.data : null,
        isGraphLoading || companies.state === 'loading',
        coHolders.edges,
        coHolders.isIncomplete,
        coHolders.isLoading
      ),
    [data, register, isGraphLoading, companies, coHolders.edges, coHolders.isIncomplete, coHolders.isLoading]
  );
}

/**
 * Shapes the two reads into what the surface renders.
 *
 * `companies` is null until the portfolio list resolves; `data` is undefined while the
 * graph read is in flight and null once it 404s.
 */
export function toGraphChartResult(
  data: ILegalStructureGraphResponse | null | undefined,
  register: ILegalStructureEntityRow[] | null | undefined,
  companies: ICompanyDataModel[] | null,
  isLoading: boolean,
  /** Holder edges the rooted forward walk could not reach — see `coHolders` (FD-4933). */
  coHolderEdges: ILegalStructureEdge[] = [],
  /** A co-holder read errored, so the merged holders are some of them rather than all. */
  isCoHoldersIncomplete = false,
  /** A co-holder read has no answer yet, so the merged chart cannot be drawn. */
  isCoHoldersLoading = false
): IStructureChartGraphResult {
  if (data) {
    // Merged before the lanes are derived, not after: a co-holder reaching exactly one
    // portfolio company earns that company's lane on its own, which is where the client's
    // own drawing puts it. Merging afterwards would leave it laneless beside the funds.
    const merged = mergeCoHolders(
      toStructureGraph(data, register ?? [], companies ?? []),
      coHolderEdges,
      register ?? [],
      companies ?? []
    );

    // Lanes are inferred from reachability — BE confirmed they are not in the payload
    // and the client should derive them.
    return {
      graph: withDerivedLanes(merged),
      // Held until the co-holder reads have answered: drawing the root's own closure first
      // and the finished chart a second later is a whole row of holders appearing and the
      // chart re-laying out under the reader on every root switch. A refetch with answers
      // in hand does not count as loading, so a write does not blank the chart.
      isLoading: isCoHoldersLoading,
      isCoHoldersIncomplete,
      isStructureUnavailable: false,
      // Read from the rooted response, not the merged graph: co-holder edges say nothing
      // about whether this root has relationships of its own.
      hasNoRelationships: data.edges.length === 0,
    };
  }

  // The company-only fallback has no edges either, but that says nothing about any
  // fund's relationships — only that the endpoint did not answer.
  return {
    graph: companies ? toCompanyOnlyGraph(companies) : EMPTY_GRAPH,
    isLoading,
    isStructureUnavailable: data === null,
    hasNoRelationships: false,
  };
}
